import { Box } from 'reflexbox';
import { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';
import CloseIcon from '@material-ui/icons/CloseRounded';
import CheckIcon from '@material-ui/icons/Check';
import SearchIcon from '@material-ui/icons/Search';
import KeyboardArrowLeftIcon from '@material-ui/icons/KeyboardArrowLeft';
import {
  RadioGroup,
  FormControlLabel,
  Radio,
  Input,
  CircularProgress,
} from '@material-ui/core';
import NotificationPopup from '../../../../../NotificationPopup';
import { HealtheraButton } from '../../../../../../healtheraComponents';
import MaterialModal from '../../../../../MaterialModal/MaterialModal';
import Table from '../../../../../Table/Table';
import { getCoordinatesByPostcode } from '../../../../../../apis/geo';
import { getPharmaciesToAssign } from '../../../../../../redux/dashboard';
import DeliveryForm from './DeliveryForm';
import coreApi from '../../../../../../apis/core';

const AssignModal = ({ open, onClose, order, onComplete }) => {
  const dispatch = useDispatch();
  const [deliveryFilter, setDeliveryFilter] = useState('delivery');
  const [postcode, setPostcode] = useState(order?.patient?.postcode);
  const [fetchingPostcode, setFetchingPostcode] = useState(false);
  const [requestDeliveryAddress, setRequestDeliveryAddress] = useState(false);
  const [pharmacyToAssign, setPharmacyToAssign] = useState(null);
  const [assignError, setAssignError] = useState(null);
  const {
    pharmacies,
    fetchingPharmacies,
  } = useSelector((state) => state.dashboard);
  const isLoading = fetchingPostcode || fetchingPharmacies;

  const handleAssignAction = async (pharmacy) => {
    setPharmacyToAssign(pharmacy);

    if (deliveryFilter === 'delivery' && !order?.delivery) {
      setRequestDeliveryAddress(true);
    } else {
      try {
        const updatedOrder = await coreApi.assignPharmacyToOrder(order._id, pharmacy.pharmacy_id, deliveryFilter);
        onComplete(updatedOrder);
        onClose();
      } catch (error) {
        setAssignError(error?.message || error?.text || error || 'Can not assign that pharmacy');
      }
    }
  };

  const handleChangePostcode = (event) => setPostcode(event.target.value);

  const handleSearchPharmacies = async (deliveryType) => {
    const type = deliveryType || deliveryFilter;

    if (type && postcode) {
      try {
        setFetchingPostcode(true);
        const coordinates = await getCoordinatesByPostcode(postcode);
        dispatch(getPharmaciesToAssign({
          type,
          ...coordinates,
          ...(order?.pharmacy_id ? { excluded_pharmacies: [order.pharmacy_id] } : {}),
        }));
      } catch (error) {
        setAssignError(error?.error || 'Invalid postcode. please try another postcode.');
      }

      setFetchingPostcode(false);
    }
  };

  const handleChangeFilter = (event) => {
    handleSearchPharmacies(event.target.value);
    setDeliveryFilter(event.target.value);
  };

  const preparePharmacyData = (pharmacies = []) => {
    const enabledIcon = (
      <DeliveryEnabled>
        <CheckIcon />
      </DeliveryEnabled>
    );

    const disabledIcon = (
      <DeliveryDisabled>
        <CloseIcon />
      </DeliveryDisabled>
    );

    return [
      {
        view: {
          pharmacyName: 'Name',
          postcode: { title: 'Postcode', width: '10%' },
          address: 'Address / Distance',
          ...(deliveryFilter === 'delivery' ? {
            local: { title: 'Local', width: '7%' },
            postal: { title: 'Postal', width: '7%' },
          } : {}),
          assignAction: { title: ' ', width: '15%' },
        },
        headers: true,
      },
      ...pharmacies.map((pharmacy) => {
        const {
          pharmacy_name: pharmacyName,
          postcode: pharmacyPostcode,
          pharmacy_address: pharmacyAddress,
          distance,
          delivery_options: {
            local_delivery = null,
            postal_delivery = null,
          } = {},
        } = pharmacy;
        const { private_prescription: localPrivatePrescription = false } = local_delivery || {};
        const { private_prescription: postalPrivatePrescription = false } = postal_delivery || {};

        return {
          view: {
            pharmacyName: (
              <Box>
                <PharmacyName>{pharmacyName}</PharmacyName>
                <OrganisationName>{pharmacy?.chain?.name}</OrganisationName>
              </Box>
            ),
            postcode: <Postcode>{pharmacyPostcode}</Postcode>,
            address: (
              <Box>
                <Address>{pharmacyAddress}</Address>
                <Distance>{distance}</Distance>
              </Box>
            ),
            ...(deliveryFilter === 'delivery' ? {
              local: !localPrivatePrescription ? disabledIcon : enabledIcon,
              postal: !postalPrivatePrescription ? disabledIcon: enabledIcon,
            } : {}),
            assignAction: (
              <HealtheraButton
                width="100px"
                height="35px"
                onClick={() => handleAssignAction(pharmacy)}
              >
                Assign
              </HealtheraButton>
            ),
          },
        };
      }),
    ];
  };

  useEffect(() => {
    (async () => {
      await handleSearchPharmacies();
    })();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <MaterialModal
      open={open}
      handleClose={onClose}
    >
      <NotificationPopup
        isOpen={!!assignError}
        message={assignError}
        autoHideDuration={3000}
        onClose={() => setAssignError(null)}
      />
      <ModalContent>
        <ModalHeader>
          <ModalHeaderTitle>
            {requestDeliveryAddress && (<BackArrowButton onClick={() => setRequestDeliveryAddress(false)} />)}
            Assign to pharmacy
          </ModalHeaderTitle>
          <CloseItem onClick={onClose} />
        </ModalHeader>
        <ModalMainContent>
          {requestDeliveryAddress ? (
            <DeliveryForm
              pharmacy={pharmacyToAssign}
              order={order}
              onConfirm={onClose}
              onComplete={onComplete}
            />
          ) : (
            <>
              <FiltersBlock>
                <RadioGroup
                  aria-label="delivery"
                  name="delivery_type"
                  onChange={handleChangeFilter}
                  value={deliveryFilter}
                  row
                >
                  <FormControlLabel
                    value="delivery"
                    control={<Radio />}
                    label="Delivery"
                  />
                  <FormControlLabel
                    value="collection"
                    control={<Radio />}
                    label="Collection"
                  />
                </RadioGroup>
                <Box>
                  <SearchInput
                    onChange={handleChangePostcode}
                    value={postcode}
                    endAdornment={<SearchButton onClick={() => handleSearchPharmacies()} />}
                    placeholder="postcode"
                    disableUnderline
                  />
                </Box>
              </FiltersBlock>
              {isLoading ? (
                <ProgressContainer>
                  <Progress />
                </ProgressContainer>
              ) : (
                <TableContent>
                  <Table data={preparePharmacyData(pharmacies)} />
                </TableContent>
              )}
            </>
          )}
        </ModalMainContent>
      </ModalContent>
    </MaterialModal>
  );
};

const ModalContent = styled(Box)`
  background: #fff;
  width: 900px;
  min-height: 700px;
`;

const ModalHeader = styled(Box)`
  border-bottom: 1px solid #E0E0E0;
  padding: 7px 15px;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const ModalHeaderTitle = styled(Box)`
  font-size: 13px;
  font-weight: 700;
  display: flex;
  align-items: center;
`;

const TableContent = styled(Box)`
  overflow: auto;
  max-height: 560px;
`;

const CloseItem = styled(CloseIcon)`
  cursor: pointer;
`;

const ModalMainContent = styled(Box)`
  padding: 7px 15px;
`;

const PharmacyName = styled(Box)`
  font-size: 12px;
  font-weight: 600;
`;

const Postcode = styled(Box)`
  font-size: 12px;
  font-weight: 600;
`;

const OrganisationName = styled(Box)`
  font-size: 12px;
  font-weight: 400;
  margin-top: 3px;
`;

const Address = styled(Box)`
  font-size: 12px;
  font-weight: 400;
`;

const Distance = styled(Box)`
  font-size: 10px;
  font-weight: 700;
  margin-top: 3px;
`;

const DeliverySupport = styled(Box)`
  padding: 5px;
  border-radius: 50%;
  width: fit-content;
  align-items: center;
  display: flex;
`;

const DeliveryEnabled = styled(DeliverySupport)`
  background: #519A711A;
  color: #519A71;
`;

const DeliveryDisabled = styled(DeliverySupport)`
  background: #EB4D3D1A;
  color: #EB4D3D;
`;

const FiltersBlock = styled(Box)`
  align-items: center;
  display: flex;
  justify-content: space-between;
`;

const ProgressContainer = styled(Box)`
  width: 100%;
  text-align: center;
  margin-top: 20px;
`;

const Progress = styled(CircularProgress)`
  color: #4DB181 !important;
`;

const SearchInput = styled(Input)`
  border: 1px solid #E2E2E2 !important;
  border-radius: 4px;
  width: 300px;
  padding: 2px 10px;
  
  &:hover, &:focus {
    border: 1px solid #E2E2E2 !important;
  }
  
  &:before {
    border: none !important;
  }
`;

const SearchButton = styled(SearchIcon)`
  cursor: pointer;
`;

const BackArrowButton = styled(KeyboardArrowLeftIcon)`
  cursor: pointer;
`;

export default AssignModal;
